import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Popover
} from '@mui/material';
import { CalendarToday, Insights } from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import { addDays, format } from 'date-fns';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar
} from 'recharts';
import { authenticatedFetch } from '../utils/api';

const RecognitionMatchGraphCard = () => {
  const today = new Date();
  const [dateRange, setDateRange] = useState([
    {
      startDate: addDays(new Date(), -7),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const buildParams = () => {
    const params = new URLSearchParams();
    params.append('start_date', format(dateRange[0].startDate, 'yyyy-MM-dd'));
    params.append('end_date', format(dateRange[0].endDate, 'yyyy-MM-dd'));
    return params.toString();
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch(`/dashboard/recognition-match-stats?${buildParams()}`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch match stats:', err);
      setError(err.message || 'Unable to load recognition stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCalendar = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleCloseCalendar = () => {
    setOpen(false);
  };

  const handleDateRangeChange = (ranges) => {
    setDateRange([ranges.selection]);
  };

  const handleApplyDateRange = () => {
    fetchStats();
    handleCloseCalendar();
  };

  const filledData = useMemo(() => {
    if (!stats?.daily_counts || !stats.date_range) {
      return [];
    }
    const filled = [];
    const current = new Date(stats.date_range.start_date);
    const end = new Date(stats.date_range.end_date);
    const map = new Map(stats.daily_counts.map(item => [item.date, item]));

    while (current <= end) {
      const dateStr = format(current, 'yyyy-MM-dd');
      const entry = map.get(dateStr) || { match_count: 0, unmatch_count: 0, possible_match_count: 0 };
      filled.push({
        date: dateStr,
        matches: entry.match_count || 0,
        unmatches: entry.unmatch_count || 0,
        possibleMatches: entry.possible_match_count || 0
      });
      current.setDate(current.getDate() + 1);
    }

    return filled;
  }, [stats]);

  return (
    <Card
      sx={{
        height: '100%',
        border: '1px solid rgba(64, 81, 138, 0.12)',
        borderRadius: 3,
        boxShadow: '0 10px 28px rgba(31, 45, 84, 0.12)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 2.25, md: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 }, mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 1.5 }, minWidth: 0 }}>
            <Box sx={{ p: { xs: 1.2, sm: 1.5 }, background: 'rgba(102, 126, 234, 0.1)', borderRadius: 2, display: 'flex', alignItems: 'center' }}>
              <Insights color="primary" sx={{ fontSize: { xs: 22, sm: 24 } }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1.25 }}>
              Known vs. Unknown Recognitions
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                px: 1.5,
                py: 0.8,
                bgcolor: 'rgba(64, 81, 138, 0.04)',
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.82rem', sm: '0.88rem' }, flex: 1 }}>
                {format(dateRange[0].startDate, 'MMM dd')} - {format(dateRange[0].endDate, 'MMM dd')}
              </Typography>
              <IconButton size="small" onClick={handleOpenCalendar}>
                <CalendarToday fontSize="small" />
              </IconButton>
            </Box>
            <Button
              variant="contained"
              onClick={fetchStats}
              disabled={loading}
              sx={{
                minWidth: { xs: '100%', sm: 88 },
                bgcolor: '#40518A',
                fontWeight: 600,
                '&:hover': { bgcolor: '#2d3961' }
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseCalendar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 0 }}>
            <DateRangePicker
              ranges={dateRange}
              onChange={handleDateRangeChange}
              moveRangeOnFirstSelection={false}
              months={1}
              direction="horizontal"
              rangeColors={['#40518A']}
              staticRanges={[]}
              inputRanges={[]}
              maxDate={today}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2, pt: 0 }}>
              <Button onClick={handleCloseCalendar} variant="outlined" size="small">
                Cancel
              </Button>
              <Button onClick={handleApplyDateRange} variant="contained" size="small">
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && stats && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25, my: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'rgba(64, 81, 138, 0.05)', border: '1px solid rgba(64, 81, 138, 0.12)', borderRadius: 1.5, px: 1.25, py: 0.8 }}>
                Total Known Users:{' '}
                <strong>{Number(stats?.totals?.matches || 0).toLocaleString()}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'rgba(64, 81, 138, 0.05)', border: '1px solid rgba(64, 81, 138, 0.12)', borderRadius: 1.5, px: 1.25, py: 0.8 }}>
                Total Unknown Users:{' '}
                <strong>{Number(stats?.totals?.unmatches || 0).toLocaleString()}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'rgba(64, 81, 138, 0.05)', border: '1px solid rgba(64, 81, 138, 0.12)', borderRadius: 1.5, px: 1.25, py: 0.8 }}>
                Total Possible Matches:{' '}
                <strong>{Number(stats?.totals?.possible_matches || 0).toLocaleString()}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'rgba(64, 81, 138, 0.05)', border: '1px solid rgba(64, 81, 138, 0.12)', borderRadius: 1.5, px: 1.25, py: 0.8 }}>
                Total Recognitions:{' '}
                <strong>{Number(stats?.totals?.recognitions || 0).toLocaleString()}</strong>
              </Typography>
            </Box>

            {filledData.length > 0 ? (
              <Box sx={{ width: '100%', minHeight: { xs: 300, sm: 360 }, pt: 0.5 }}>
                <ResponsiveContainer width="100%" height={360}>
                  <BarChart data={filledData} margin={{ top: 8, right: 10, left: -8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#d9e0ee" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                      tick={{ fontSize: 11, fill: '#667085' }}
                      interval="preserveStartEnd"
                      minTickGap={24}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#667085' }} />
                  <Tooltip cursor={{ stroke: '#000', fill: 'none', strokeWidth: 0 }} />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '6px' }} />
                  <Bar dataKey="matches" name="Known Users" stackId="recognitions" fill="#10b981" />
                  <Bar dataKey="unmatches" name="Unknown Users" stackId="recognitions" fill="#ef4444" />
                  <Bar dataKey="possibleMatches" name="Possible Match" stackId="recognitions" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 320,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.default'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No recognition data available for the selected period.
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RecognitionMatchGraphCard;
